import React, { useState, useRef } from 'react';
import { cloudinaryForDisplay } from '../utils/cloudinary';
import AgentBrandCard from '../components/AgentBrandCard';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://peekr-production.up.railway.app';

export default function GalleryScreen({ gallery, eventName, roomCode, galleryInfo, agentBrand }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showBestShots, setShowBestShots] = useState(false);
  const [bestShots, setBestShots] = useState([]);
  const [aiError, setAiError] = useState(null);
  // Wave 3 AI Studio — results generated on demand via the origin-checked
  // server proxies (the Anthropic key stays server-side).
  const [shotDir, setShotDir] = useState(null);
  const [listingDesc, setListingDesc] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [neighborhood, setNeighborhood] = useState(null);
  const [threads, setThreads] = useState(null);     // celebration: per-contributor stories
  const [moodArc, setMoodArc] = useState(null);     // celebration: vibe graph {points, summary}
  const [aiBusy, setAiBusy] = useState(null);       // feature key currently generating
  const [openPanel, setOpenPanel] = useState(null); // which result panel is shown
  const carouselRef = useRef(null);
  const touchStartX = useRef(null);
  
  const displayPhotos = showBestShots && bestShots.length > 0 ? bestShots : gallery;

  // Event without listing specs → celebration framing: Memories Studio instead
  // of Listing Studio, album copy instead of listing copy.
  const celebration = !!galleryInfo?.celebration;
  
  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Generic AI Studio call → origin-checked server proxy. The browser never
  // holds the Anthropic key; the server forwards with its own. Each feature
  // POSTs to its own /…-proxy route and renders the JSON below the buttons.
  const callAI = async (key, path, onData, body = {}) => {
    if (aiBusy) return;
    setAiBusy(key);
    setAiError(null);
    setOpenPanel(key);
    try {
      const res = await fetch(`${SERVER_URL}/gallery/${roomCode}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI request failed');
      onData(data);
    } catch (err) {
      setAiError({ key, msg: err.message });
      console.error(`AI ${key} error:`, err);
    } finally {
      setAiBusy(null);
    }
  };

  const runBestShots    = () => callAI('best',    'best-shots-proxy',    (d) => { setBestShots(d.bestShots || []); setShowBestShots(true); }, { maxPhotos: 10 });
  const runShotDir      = () => callAI('shots',   'shot-director-proxy', (d) => setShotDir(d));
  const runListingDesc  = () => callAI('listing',    'listing-description-proxy', (d) => setListingDesc(d.description || ''));
  const runHighlights   = () => callAI('highlights', 'highlights-proxy',         (d) => setHighlights(d.highlights || []));
  const runNeighborhood = () => callAI('neighborhood', 'neighborhood-proxy',     (d) => setNeighborhood(d.blurb || ''));
  const runThreads      = () => callAI('threads', 'guest-threads-proxy',         (d) => setThreads(d.threads || []));
  const runMood         = () => callAI('mood',    'mood-arc-proxy',              (d) => setMoodArc(d));
  const openMemoryBook  = () => window.open(`${SERVER_URL}/gallery/${roomCode}/memory-book`, '_blank');

  // ── AI result panel renderers ─────────────────────────────────────────
  const panelText = { fontSize: 13, color: '#C9D6E2', lineHeight: 1.55, textAlign: 'center' };
  const copyBtnStyle = { marginTop: 12, padding: '8px 16px', borderRadius: 20, border: '1px solid rgba(0,224,255,0.4)', background: 'rgba(0,224,255,0.08)', color: '#00E0FF', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
  const panelLoading = (label) => (
    <div style={{ marginTop: 18, textAlign: 'center' }}>
      <div style={{ display: 'inline-flex', gap: 6 }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#00E0FF', display: 'inline-block', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: '#8A9BB0' }}>{label}</div>
    </div>
  );

  const renderShotsPanel = () => {
    if (aiBusy === 'shots') return panelLoading('Scanning your coverage…');
    if (!shotDir) return null;
    const missing = shotDir.missing || [];
    return (
      <div style={{ marginTop: 16, textAlign: 'left' }}>
        {shotDir.summary && <div style={{ ...panelText, textAlign: 'left', marginBottom: 12 }}>{shotDir.summary}</div>}
        {shotDir.priority && (
          <div style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 20, marginBottom: 12, fontSize: 12, fontWeight: 700, color: '#070A12', background: 'linear-gradient(135deg, #00E0FF, #6E7BFF)' }}>
            🎯 Get next: {shotDir.priority}
          </div>
        )}
        {missing.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {missing.map((m, i) => (
              <div key={i} style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#EAF1F8', marginBottom: 2 }}>📸 {m.shot}</div>
                <div style={{ fontSize: 12, color: '#8A9BB0', lineHeight: 1.45 }}>{m.why}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderListingPanel = () => {
    if (aiBusy === 'listing') return panelLoading('Writing your listing description…');
    if (listingDesc == null) return null;
    return (
      <div style={{ marginTop: 16, textAlign: 'left' }}>
        <div style={{ ...panelText, textAlign: 'left', whiteSpace: 'pre-wrap' }}>{listingDesc}</div>
        <button onClick={() => navigator.clipboard?.writeText(listingDesc)} style={copyBtnStyle}>📋 Copy description</button>
      </div>
    );
  };

  const renderHighlightsPanel = () => {
    if (aiBusy === 'highlights') return panelLoading('Finding the standout features…');
    if (!highlights) return null;
    return (
      <div style={{ marginTop: 16, textAlign: 'left' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {highlights.map((h, i) => (
            <div key={i} style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', fontSize: 13, color: '#EAF1F8' }}>✦ {h}</div>
          ))}
        </div>
        <button onClick={() => navigator.clipboard?.writeText(highlights.map((h) => '• ' + h).join('\n'))} style={copyBtnStyle}>📋 Copy highlights</button>
      </div>
    );
  };

  const renderNeighborhoodPanel = () => {
    if (aiBusy === 'neighborhood') return panelLoading('Researching the neighborhood…');
    if (neighborhood == null) return null;
    return (
      <div style={{ marginTop: 16, textAlign: 'left' }}>
        <div style={{ ...panelText, textAlign: 'left', whiteSpace: 'pre-wrap' }}>{neighborhood}</div>
        <button onClick={() => navigator.clipboard?.writeText(neighborhood)} style={copyBtnStyle}>📋 Copy blurb</button>
      </div>
    );
  };

  const renderThreadsPanel = () => {
    if (aiBusy === 'threads') return panelLoading("Weaving everyone's story…");
    if (threads == null) return null;
    if (threads.length === 0) {
      return <div style={{ marginTop: 16, fontSize: 13, color: '#8A9BB0', textAlign: 'center' }}>Need a few photos from at least two people for this one — invite guests to share theirs!</div>;
    }
    return (
      <div style={{ marginTop: 16, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {threads.map((t, i) => (
          <div key={i} style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#EAF1F8', marginBottom: 4 }}>
              💬 {t.contributor} <span style={{ color: '#5A6C82', fontWeight: 400 }}>· {t.photoCount} photo{t.photoCount !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ ...panelText, textAlign: 'left' }}>{t.narrative}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderMoodPanel = () => {
    if (aiBusy === 'mood') return panelLoading('Reading the room…');
    if (moodArc == null) return null;
    const points = moodArc.points || [];
    return (
      <div style={{ marginTop: 16, textAlign: 'left' }}>
        {points.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {points.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 70, fontSize: 11, color: '#8A9BB0', textTransform: 'capitalize', flexShrink: 0 }}>{p.label}</div>
                <div style={{ flex: 1, height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.max(4, p.score)}%`, height: '100%', borderRadius: 5, background: 'linear-gradient(90deg, #6E7BFF, #00E0FF)' }} />
                </div>
                <div style={{ width: 30, fontSize: 11, color: '#EAF1F8', textAlign: 'right', flexShrink: 0 }}>{p.score}</div>
              </div>
            ))}
          </div>
        )}
        {moodArc.summary && <div style={{ ...panelText, textAlign: 'left' }}>{moodArc.summary}</div>}
      </div>
    );
  };

  // Shared tile renderer — used by both the Listing Studio and Memories Studio grids.
  const renderTiles = (tiles) => tiles.map((f) => {
    const active = openPanel === f.key;
    return (
      <button
        key={f.key}
        onClick={f.onClick}
        disabled={f.disabled || !!aiBusy}
        style={{
          textAlign: 'left', padding: '12px 14px', borderRadius: 14,
          cursor: f.disabled ? 'not-allowed' : 'pointer',
          background: active ? 'linear-gradient(135deg, rgba(0,224,255,0.14), rgba(110,123,255,0.14))' : 'rgba(255,255,255,0.03)',
          border: active ? '1px solid rgba(0,224,255,0.5)' : '1px solid rgba(255,255,255,0.08)',
          opacity: f.disabled ? 0.4 : 1, transition: 'all 0.2s ease', color: '#EAF1F8',
        }}
      >
        <div style={{ fontSize: 20, marginBottom: 4 }}>{f.icon}</div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {aiBusy === f.key ? 'Thinking…' : f.label}
        </div>
        <div style={{ fontSize: 11, color: '#8A9BB0', marginTop: 2 }}>
          {f.sub}
        </div>
      </button>
    );
  });

  const handleDownloadAll = () => {
    const photos = showBestShots && bestShots.length > 0 ? bestShots : gallery;
    photos.forEach((photo, i) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = photo.url;
        link.download = `${eventName || roomCode}_photo_${i + 1}.jpg`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, i * 500);
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${eventName || roomCode} — ${celebration ? 'the album' : 'Listing Photos'}`,
        text: celebration
          ? `Relive ${eventName || roomCode} — photos from the day 🎉`
          : `See the photos for ${eventName || roomCode}!`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(celebration ? 'Album link copied!' : 'Listing link copied to clipboard!');
    }
  };

  const handleDownloadPhoto = (photo, index) => {
    const link = document.createElement('a');
    link.href = photo.url;
    link.download = `${eventName || roomCode}_photo_${index + 1}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openLightbox = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedIndex(index);
  };

  const lightboxNext = (e) => {
    e.stopPropagation();
    const photos = displayPhotos;
    const next = (selectedIndex + 1) % photos.length;
    setSelectedPhoto(photos[next]);
    setSelectedIndex(next);
  };

  const lightboxPrev = (e) => {
    e.stopPropagation();
    const photos = displayPhotos;
    const prev = (selectedIndex - 1 + photos.length) % photos.length;
    setSelectedPhoto(photos[prev]);
    setSelectedIndex(prev);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e, next, prev) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? next(e) : prev(e);
    }
    touchStartX.current = null;
  };

  return (
    <div className="screen" style={{ padding: '20px', paddingTop: '40px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: '#6A85A0', letterSpacing: '3px', marginBottom: '8px' }}>
          {galleryInfo?.isLive
            ? (celebration ? '🔴 LIVE CELEBRATION' : '🔴 LIVE SHOWING')
            : (celebration ? 'CELEBRATION ALBUM' : 'LISTING PHOTOS')}
        </div>
        <div className="brand" style={{ fontSize: '28px', marginBottom: '8px' }}>
          {eventName || roomCode}
        </div>
        <div style={{ color: '#6A85A0', fontSize: '14px' }}>
          📸 {gallery.length} photos
        </div>
      </div>

      {/* ✨ MEMORIES STUDIO (celebrations) — keepsakes generated on demand
          through the same origin-checked proxies as the Listing Studio. */}
      {gallery.length > 0 && celebration && (
        <div className="card" style={{ maxWidth: 600, margin: '0 auto 24px', padding: '20px 18px' }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>✨ Memories Studio</div>
          <div style={{ fontSize: 13, color: '#8A9BB0', marginBottom: 16, lineHeight: 1.5 }}>
            {gallery.length} photo{gallery.length !== 1 ? 's' : ''} from the day — turn them into keepsakes.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {renderTiles([
              { key: 'best',    label: 'Best Moments',     sub: 'AI picks the standout shots',        icon: '🌟', onClick: runBestShots, disabled: false },
              { key: 'threads', label: 'Story of the Day', sub: "Everyone's photos, everyone's view", icon: '💬', onClick: runThreads,   disabled: false },
              { key: 'mood',    label: 'Vibe Graph',       sub: 'The energy, moment by moment',       icon: '📈', onClick: runMood,      disabled: false },
              ...(galleryInfo?.recapReady ? [{ key: 'book', label: 'Memory Book', sub: 'The story of the day, beautifully told', icon: '📖', onClick: openMemoryBook, disabled: false }] : []),
            ])}
          </div>

          {aiError && (
            <div style={{ marginTop: 14, fontSize: 13, color: '#FF6B8A', textAlign: 'center' }}>
              {aiError.msg}
            </div>
          )}

          {!aiError && openPanel === 'threads' && renderThreadsPanel()}
          {!aiError && openPanel === 'mood' && renderMoodPanel()}
          {!aiError && openPanel === 'best' && (
            aiBusy === 'best'
              ? panelLoading('Picking the keepers…')
              : showBestShots && (
                <div style={{ marginTop: 14, fontSize: 13, color: '#00E0FF', textAlign: 'center' }}>
                  ⭐ Showing {bestShots.length} AI-picked best moments below ·{' '}
                  <span onClick={() => { setShowBestShots(false); setOpenPanel(null); }} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8A9BB0' }}>show all</span>
                </div>
              )
          )}
        </div>
      )}

      {/* ✨ LISTING STUDIO — on-demand listing copy generated by Claude through
          the origin-checked server proxies (key stays server-side; every call is
          bounded by the per-event AI budget + 48h gallery TTL). */}
      {gallery.length > 0 && !celebration && (
        <div className="card" style={{ maxWidth: 600, margin: '0 auto 24px', padding: '20px 18px' }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>✨ Listing Studio</div>
          <div style={{ fontSize: 13, color: '#8A9BB0', marginBottom: 16, lineHeight: 1.5 }}>
            Turn {gallery.length} photo{gallery.length !== 1 ? 's' : ''} into listing-ready copy.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {[
              { key: 'listing',    label: 'Listing Description', sub: 'MLS-ready copy from your photos', icon: '📝', onClick: runListingDesc, disabled: false },
              { key: 'highlights', label: 'Property Highlights', sub: 'Standout selling points',         icon: '✨', onClick: runHighlights,  disabled: false },
              { key: 'best',    label: 'Hero Shots',     sub: 'AI picks the best listing photos', icon: '🌟', onClick: runBestShots, disabled: false },
              { key: 'shots',   label: 'Coverage Coach',  sub: 'What buyers expect but is missing', icon: '🎬', onClick: runShotDir,   disabled: false },
              { key: 'neighborhood', label: 'Neighborhood', sub: 'Factual area summary for buyers', icon: '🗺️', onClick: runNeighborhood, disabled: false },
            ].map((f) => {
              const active = openPanel === f.key;
              return (
                <button
                  key={f.key}
                  onClick={f.onClick}
                  disabled={f.disabled || !!aiBusy}
                  style={{
                    textAlign: 'left', padding: '12px 14px', borderRadius: 14,
                    cursor: f.disabled ? 'not-allowed' : 'pointer',
                    background: active ? 'linear-gradient(135deg, rgba(0,224,255,0.14), rgba(110,123,255,0.14))' : 'rgba(255,255,255,0.03)',
                    border: active ? '1px solid rgba(0,224,255,0.5)' : '1px solid rgba(255,255,255,0.08)',
                    opacity: f.disabled ? 0.4 : 1, transition: 'all 0.2s ease', color: '#EAF1F8',
                  }}
                >
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{f.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {aiBusy === f.key ? 'Thinking…' : f.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#8A9BB0', marginTop: 2 }}>
                    {f.sub}
                  </div>
                </button>
              );
            })}
          </div>

          {aiError && (
            <div style={{ marginTop: 14, fontSize: 13, color: '#FF6B8A', textAlign: 'center' }}>
              {aiError.msg}
            </div>
          )}

          {!aiError && openPanel === 'shots' && renderShotsPanel()}
          {!aiError && openPanel === 'listing' && renderListingPanel()}
          {!aiError && openPanel === 'highlights' && renderHighlightsPanel()}
          {!aiError && openPanel === 'neighborhood' && renderNeighborhoodPanel()}
          {!aiError && openPanel === 'best' && (
            aiBusy === 'best'
              ? panelLoading('Picking the keepers…')
              : showBestShots && (
                <div style={{ marginTop: 14, fontSize: 13, color: '#00E0FF', textAlign: 'center' }}>
                  ⭐ Showing {bestShots.length} AI-picked hero shots below ·{' '}
                  <span onClick={() => { setShowBestShots(false); setOpenPanel(null); }} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8A9BB0' }}>show all</span>
                </div>
              )
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', justifyContent: 'center' }}>
        <button 
          className="btn" 
          onClick={handleShare}
          style={{ padding: '10px 20px', fontSize: '14px' }}
        >
          🔗 Share
        </button>
        {gallery.length > 0 && (
          <button 
            onClick={handleDownloadAll}
            style={{ 
              padding: '10px 20px', 
              fontSize: '14px',
              background: 'transparent',
              border: '1px solid #00D4FF',
              color: '#00D4FF',
              borderRadius: '25px',
              cursor: 'pointer',
            }}
          >
            📥 Download {showBestShots && bestShots.length > 0 ? 'Hero' : 'All'}
          </button>
        )}
      </div>

      {/* Photo carousel — horizontal scroll */}
      {displayPhotos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6A85A0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
          <div>No photos yet</div>
          <div style={{ fontSize: '13px', marginTop: '8px' }}>
            {celebration ? "Photos from the celebration will appear here as they're shared" : "Photos will appear here as they're added"}
          </div>
        </div>
      ) : (
        <>
          {/* Horizontal scroll strip */}
          <div
            ref={carouselRef}
            style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '10px',
              padding: '8px 4px 16px',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            {displayPhotos.map((photo, i) => (
              <div
                key={photo.url + i}
                style={{
                  flex: '0 0 auto',
                  width: '200px',
                  height: '200px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  scrollSnapAlign: 'start',
                  border: showBestShots ? '2px solid #8B5CF6' : '1px solid #122038',
                  position: 'relative',
                }}
                onClick={() => openLightbox(photo, i)}
              >
                {showBestShots && (
                  <div style={{
                    position: 'absolute', top: '8px', left: '8px',
                    background: 'linear-gradient(135deg, #8B5CF6, #00D4FF)',
                    padding: '2px 8px', borderRadius: '10px',
                    fontSize: '11px', fontWeight: 'bold', zIndex: 2,
                  }}>#{i + 1}</div>
                )}
                <img
                  src={cloudinaryForDisplay(photo.url, 400)}
                  alt={`View ${i + 1}`}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '5px 8px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.78))',
                  fontSize: '10px', color: '#D6E0EA',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {photo.caption ? `✨ ${photo.caption}` : photo.uploadedBy}
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', fontSize: '12px', color: '#3A5570', marginBottom: '8px' }}>
            ← Swipe to browse · Tap to open ·  {displayPhotos.length} photo{displayPhotos.length !== 1 ? 's' : ''}
          </div>
        </>
      )}

      {/* Lightbox with swipe + prev/next + download */}
      {selectedPhoto && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.97)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px',
          }}
          onClick={() => setSelectedPhoto(null)}
          onTouchStart={handleTouchStart}
          onTouchEnd={(e) => handleTouchEnd(e, lightboxNext, lightboxPrev)}
        >
          {/* Close */}
          <button onClick={() => setSelectedPhoto(null)} style={{
            position: 'absolute', top: '20px', right: '20px',
            background: 'transparent', border: 'none',
            color: '#FFFFFF', fontSize: '32px', cursor: 'pointer',
          }}>✕</button>

          {/* Counter */}
          <div style={{
            position: 'absolute', top: '24px', left: '50%',
            transform: 'translateX(-50%)',
            color: '#6A85A0', fontSize: '13px',
          }}>
            {selectedIndex + 1} / {displayPhotos.length}
          </div>

          {/* Prev arrow */}
          {displayPhotos.length > 1 && (
            <button onClick={lightboxPrev} style={{
              position: 'absolute', left: '12px', top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.1)', border: 'none',
              color: '#FFFFFF', fontSize: '24px', cursor: 'pointer',
              borderRadius: '50%', width: '44px', height: '44px',
            }}>‹</button>
          )}

          {/* Image */}
          <img
                  src={cloudinaryForDisplay(selectedPhoto.url, 1920)}
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  alt="Selected"
            style={{
              maxWidth: '100%', maxHeight: '72vh',
              objectFit: 'contain', borderRadius: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next arrow */}
          {displayPhotos.length > 1 && (
            <button onClick={lightboxNext} style={{
              position: 'absolute', right: '12px', top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.1)', border: 'none',
              color: '#FFFFFF', fontSize: '24px', cursor: 'pointer',
              borderRadius: '50%', width: '44px', height: '44px',
            }}>›</button>
          )}

          {/* Meta + download */}
          <div style={{
            marginTop: '16px', display: 'flex',
            flexDirection: 'column', alignItems: 'center', gap: '10px',
          }} onClick={(e) => e.stopPropagation()}>
            {selectedPhoto.caption && (
              <div style={{ color: '#E8EEF4', fontSize: '15px', fontStyle: 'italic', textAlign: 'center', maxWidth: '92%', lineHeight: 1.45 }}>
                “{selectedPhoto.caption}”
              </div>
            )}
            <div style={{ color: '#6A85A0', fontSize: '13px' }}>
              By {selectedPhoto.uploadedBy} · {formatTime(selectedPhoto.ts)}
            </div>
            <button
              onClick={() => handleDownloadPhoto(selectedPhoto, selectedIndex)}
              style={{
                padding: '10px 24px', background: '#00D4FF',
                border: 'none', color: '#050C1A',
                borderRadius: '25px', cursor: 'pointer',
                fontWeight: 'bold', fontSize: '14px',
              }}
            >
              📥 Download This Photo
            </button>
          </div>
        </div>
      )}

      {/* Footer — the agent's brand card when present, else the Peekr CTA */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        {agentBrand ? (
          <AgentBrandCard agentBrand={agentBrand} />
        ) : (
          <div className="card" style={{ padding: '20px', maxWidth: '320px', margin: '0 auto' }}>
            <div style={{ fontSize: '13px', color: '#6A85A0', marginBottom: '12px' }}>
              {celebration ? 'Hosting a celebration?' : 'Are you an agent?'}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#00D4FF', letterSpacing: '2px', marginBottom: '8px' }}>
              Peekr
            </div>
            <div style={{ fontSize: '13px', color: '#6A85A0', marginBottom: '16px' }}>
              {celebration
                ? 'Stream it live to family anywhere — no app for them. Keepsake album after.'
                : 'Run live property showings buyers watch from anywhere.'}
            </div>
            <a
              href={celebration ? '/celebrate.html' : 'https://getpeekr.com'}
              className="btn"
              style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}
            >
              {celebration ? 'Peekr for Celebrations' : 'Get Peekr for Agents'}
            </a>
          </div>
        )}
      </div>

      <div className="sub" style={{ marginTop: '24px', textAlign: 'center' }}>
        {galleryInfo?.expiresAt && (
          <div style={{ marginBottom: '8px' }}>
            {celebration ? 'Album available until' : 'Listing photos available until'} {new Date(galleryInfo.expiresAt).toLocaleDateString()}
          </div>
        )}
        getpeekr.com
      </div>
    </div>
  );
}
